import AsyncStorage from "@react-native-async-storage/async-storage";

const HABITS_KEY = "custom_habits_v1";
const LOGS_KEY = "custom_habit_logs_v1";

/** @typedef {{ id: string, name: string, icon: string, dailyGoal: number, color: string, createdAt: string }} CustomHabit */
/** @typedef {Record<string, Record<string, number>>} HabitLogs */

export const HABIT_ICON_OPTIONS = [
  "🏆",
  "📖",
  "🧠",
  "🚿",
  "☀️",
  "🍱",
  "✈️",
  "⚔️",
  "📷",
  "🍃",
  "🥕",
  "💧",
  "🎯",
  "📝",
  "🔥",
  "👟",
  "⚡",
  "🎵",
  "💊",
  "🛏️",
];

export const HABIT_RING_COLORS = ["#F97316", "#14B8A6", "#8B5CF6", "#3B82F6", "#EC4899", "#6366F1"];

/** @type {CustomHabit[]} */
export const DEFAULT_CUSTOM_HABITS = [
  {
    id: "habit_workout",
    name: "Workout",
    icon: "🏆",
    dailyGoal: 1,
    color: "#F97316",
    createdAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "habit_reading",
    name: "Reading",
    icon: "📖",
    dailyGoal: 1,
    color: "#14B8A6",
    createdAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "habit_meditation",
    name: "Meditation",
    icon: "🧠",
    dailyGoal: 1,
    color: "#8B5CF6",
    createdAt: "2026-01-01T00:00:00.000Z",
  },
];

export function todayDateKey() {
  return new Date().toISOString().split("T")[0];
}

export function newHabitId() {
  return `habit_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * @param {unknown} raw
 * @returns {CustomHabit[]}
 */
function normalizeHabits(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((h) => h && typeof h === "object")
    .map((h) => ({
      id: String(h.id || newHabitId()),
      name: String(h.name || "Habit").slice(0, 15),
      icon: HABIT_ICON_OPTIONS.includes(h.icon) ? h.icon : "🎯",
      dailyGoal: Math.max(1, Math.min(99, Number(h.dailyGoal) || 1)),
      color: typeof h.color === "string" && h.color.startsWith("#") ? h.color : HABIT_RING_COLORS[0],
      createdAt: typeof h.createdAt === "string" ? h.createdAt : new Date().toISOString(),
    }));
}

/**
 * @param {unknown} raw
 * @returns {HabitLogs}
 */
function normalizeLogs(raw) {
  if (!raw || typeof raw !== "object") return {};
  /** @type {HabitLogs} */
  const out = {};
  for (const [dateKey, day] of Object.entries(raw)) {
    if (!day || typeof day !== "object") continue;
    /** @type {Record<string, number>} */
    const row = {};
    for (const [habitId, count] of Object.entries(day)) {
      const n = Number(count);
      if (Number.isFinite(n) && n >= 0) row[habitId] = Math.floor(n);
    }
    if (Object.keys(row).length) out[dateKey] = row;
  }
  return out;
}

/** @returns {Promise<CustomHabit[]>} */
export async function loadCustomHabits() {
  try {
    const raw = await AsyncStorage.getItem(HABITS_KEY);
    if (!raw) {
      await saveCustomHabits(DEFAULT_CUSTOM_HABITS);
      return [...DEFAULT_CUSTOM_HABITS];
    }
    const parsed = normalizeHabits(JSON.parse(raw));
    return parsed.length ? parsed : [...DEFAULT_CUSTOM_HABITS];
  } catch {
    return [...DEFAULT_CUSTOM_HABITS];
  }
}

/** @param {CustomHabit[]} habits */
export async function saveCustomHabits(habits) {
  await AsyncStorage.setItem(HABITS_KEY, JSON.stringify(habits));
}

/** @returns {Promise<HabitLogs>} */
export async function loadHabitLogs() {
  try {
    const raw = await AsyncStorage.getItem(LOGS_KEY);
    if (!raw) return {};
    return normalizeLogs(JSON.parse(raw));
  } catch {
    return {};
  }
}

/** @param {HabitLogs} logs */
export async function saveHabitLogs(logs) {
  await AsyncStorage.setItem(LOGS_KEY, JSON.stringify(logs));
}

/**
 * @param {HabitLogs} logs
 * @param {string} habitId
 * @param {string} [dateKey]
 */
export function getHabitCount(logs, habitId, dateKey = todayDateKey()) {
  return logs[dateKey]?.[habitId] ?? 0;
}

/**
 * @param {HabitLogs} logs
 * @param {string} habitId
 * @param {number} count
 * @param {string} [dateKey]
 * @returns {HabitLogs}
 */
export function setHabitCount(logs, habitId, count, dateKey = todayDateKey()) {
  const next = { ...logs };
  const day = { ...(next[dateKey] || {}) };
  const safe = Math.max(0, Math.min(999, Math.floor(count)));
  if (safe === 0) {
    delete day[habitId];
  } else {
    day[habitId] = safe;
  }
  if (Object.keys(day).length === 0) delete next[dateKey];
  else next[dateKey] = day;
  return next;
}

/**
 * Habits that met or exceeded today's daily goal.
 * @param {CustomHabit[]} habits
 * @param {HabitLogs} logs
 * @param {string} [dateKey]
 */
export function countCompletedSessions(habits, logs, dateKey = todayDateKey()) {
  return habits.filter((h) => getHabitCount(logs, h.id, dateKey) >= h.dailyGoal).length;
}

/**
 * @param {CustomHabit[]} habits
 * @param {number} nextIndex
 */
export function pickHabitColor(habits, nextIndex = habits.length) {
  return HABIT_RING_COLORS[nextIndex % HABIT_RING_COLORS.length];
}
